import path from "node:path";
import type { Locator, Response } from "@iflyrpa/share";
import { downloadImage, getFilenameFromUrl, response, success } from "@iflyrpa/share";
import { TimeFormatter } from "../../utils";
import type { PublishAction } from "./index";

/**
 * 视频号发布 - RPA 方式
 * 通过浏览器自动化实现视频号内容发布
 */
export const rpaAction: PublishAction = async (task, params) => {
    task.logger.info("开始微信视频号发布（RPA 模式）");

    const tmpCachePath = task.getTmpPath();

    const page = await task.createPage({
        url: "https://channels.weixin.qq.com/platform/post/finderNewLifeCreate",
        show: task.debug,
        cookies: params.cookies,
    });

    try {
        const waitForElement = async (selector: string, timeout = 10000): Promise<Locator | null> => {
            try {
                const element = page.locator(selector);
                await element.waitFor({ state: "visible", timeout });
                return element;
            } catch {
                task.logger.warn(`元素未找到: ${selector}`);
                return null;
            }
        };

        // 重试执行
        const retryAction = async <T>(action: () => Promise<T>, maxRetries = 3, delay = 1000): Promise<T> => {
            let lastError: Error | undefined;
            for (let i = 0; i < maxRetries; i++) {
                try {
                    return await action();
                } catch (error) {
                    lastError = error as Error;
                    task.logger.warn(`重试 ${i + 1}/${maxRetries}: ${lastError.message}`);
                    if (i < maxRetries - 1) {
                        await page.waitForTimeout(delay);
                    }
                }
            }
            throw lastError;
        };

        // 页面初始化

        task.logger.info("等待页面加载...");

        // 检查登录状态
        task.logger.info("检查登录状态...");

        // 打印当前 URL 和页面标题
        if (task.debug) {
            task.logger.info(`当前页面 URL: ${page.url()}`);
            const title = await page.title();
            task.logger.info(`页面标题: ${title}`);
        }

        try {
            await page.waitForSelector(".post-edit-wrap", { state: "visible", timeout: 10000 });
            task.logger.info("✓ 登录状态正常，找到编辑器容器");
        } catch {
            task.logger.error("✗ 未找到编辑器，可能登录失效");
            return { code: 414, message: "登录失效或页面加载异常", data: page.url() };
        }

        task.logger.info("页面加载完成，开始填充内容");

        // 上传图片
        if (params.banners && params.banners.length > 0) {
            task.logger.info(`开始上传 ${params.banners.length} 张图片`);

            await retryAction(async () => {
                // 下载图片到本地
                const images = await Promise.all(
                    params.banners.map((url) => {
                        const fileName = getFilenameFromUrl(url);
                        return downloadImage(url, path.join(tmpCachePath, fileName));
                    }),
                );

                // 根据实际 HTML 结构，文件上传输入框在 .ant-upload-btn 内
                // 使用 page.locator 直接定位隐藏的 input，不需要等待可见性
                const uploadSelectors = [
                    '.ant-upload-btn input[type="file"]',
                    '.upload input[type="file"][accept*="image"]',
                    '.upload-wrap input[type="file"]',
                    'input[type="file"][accept*="image"]',
                ];

                let uploadInput: Locator | null = null;

                // 调试：打印当前页面所有的 file input
                if (task.debug) {
                    const allFileInputs = await page.locator('input[type="file"]').count();
                    task.logger.info(`页面中共找到 ${allFileInputs} 个文件上传输入框`);

                    // 打印页面 URL 确认是否在正确页面
                    task.logger.info(`当前页面 URL: ${page.url()}`);
                }

                for (const selector of uploadSelectors) {
                    const input = page.locator(selector);
                    const count = await input.count();
                    if (count > 0) {
                        uploadInput = input.first();
                        task.logger.info(`找到上传输入框: ${selector}`);
                        break;
                    }
                }

                if (!uploadInput) {
                    // 如果还是找不到，尝试等待更长时间后再找一次
                    task.logger.warn("未找到上传输入框，等待 3 秒后重试...");
                    await page.waitForTimeout(3000);

                    // 直接查找任意 file input（不检查可见性）
                    const anyFileInput = page.locator('input[type="file"]');
                    const inputCount = await anyFileInput.count();

                    if (inputCount > 0) {
                        uploadInput = anyFileInput.first();
                        task.logger.info(`找到文件输入框（共 ${inputCount} 个）`);
                    } else {
                        // 截图以便调试
                        if (task.debug) {
                            const screenshotPath = path.join(tmpCachePath, `upload-error-${Date.now()}.png`);
                            await page.screenshot({ path: screenshotPath, fullPage: true });
                            task.logger.error(`未找到上传输入框，已截图保存至: ${screenshotPath}`);
                        }
                        throw new Error("未找到图片上传输入框");
                    }
                }

                // 上传文件
                await uploadInput.setInputFiles(images);
                task.logger.info(`图片上传成功: ${images.length} 张`);

                // 等待图片处理
                await page.waitForTimeout(3000);
            });
        }

        // 填写标题
        if (params.title) {
            task.logger.info(`填写标题: ${params.title}`);
            await retryAction(async () => {
                const titleInput = await waitForElement('input[placeholder*="填写标题"]', 10000);

                if (!titleInput) {
                    throw new Error("未找到标题输入框");
                }

                await titleInput.click();
                // 先清空（重试时避免重复）
                await titleInput.clear({ timeout: 3000 });
                await titleInput.fill(params.title, { timeout: 5000 });
                task.logger.info("标题填写完成");
            });
        }

        // 填写描述和话题
        if (params.content) {
            task.logger.info("填写描述和话题");
            await retryAction(async () => {
                const descEditor = await waitForElement(".input-editor", 10000);
                if (!descEditor) {
                    throw new Error("未找到描述编辑器");
                }
                await descEditor.click();
                await page.waitForTimeout(500);

                // 清空编辑器内容（重试时避免重复输入）
                await descEditor.evaluate((el) => {
                    el.textContent = "";
                });
                await page.waitForTimeout(300);
                task.logger.info("已清空编辑器内容");

                // 模拟用户逐字输入，触发富文本编辑器的话题识别
                await descEditor.pressSequentially(params.content, { delay: 10 }); // delay 设置输入延迟，模拟真实输入
                await page.waitForTimeout(500);

                task.logger.info("内容填写完成");
            });
        }

        //  选择地点
        if (params.address?.poi_id) {
            task.logger.info(`选择地点: ${params.address.name}`);
            const instance = page.locator(".position-display-wrap");
            await instance.click();
            await page.waitForTimeout(1000);
            await page
                .locator(".location-filter-wrap input")
                .fill(params.address?.name || params.address?.address || params.address?.city_name);
            await page.waitForTimeout(2000);
            const poperInstance = page.locator(".location-filter-wrap .common-option-list-wrap .option-item");
            await poperInstance.nth(1).waitFor(); // 等待第二个元素出现
            await poperInstance.nth(1).click(); // 点击第二个元素（索引从0开始，1是第二个）
            task.logger.info("地点选择完成");
        }

        // 选择合集
        if (params.collection) {
            task.logger.info(`选择合集: ${params.collection.collectionName}`);
            const instanceCollection = page.locator(".post-album-display-wrap");
            await instanceCollection.click();
            await page.waitForTimeout(1000);
            page.locator(".post-album-wrap .option-item")
                .filter({ hasText: params.collection.collectionName })
                .first()
                .click({ force: true });
        }

        // 填写链接
        if (params.link) {
            const instanceLink = page.locator(".link-display-wrap");
            await instanceLink.first().click();
            task.logger.info(`添加链接: ${params.link.link}`);
            await page.waitForTimeout(500);
            const linkTypeName = params.link.urlType === 1 ? "公众号文章" : "红包封面";
            page.locator(".post-link-wrap .link-list-options .link-option-item")
                .filter({ hasText: linkTypeName })
                .first()
                .click({ force: true });
            await waitForElement(".link-input-wrap", 3000);
            const linkInput = page.locator('.link-input-wrap input[type="text"]');
            await linkInput.first().fill(params.link.link);
        }

        // 选择音乐
        if (params.music) {
            task.logger.info(`添加背景音乐: ${params.music.name}`);
            const musicFormItem = page.locator(".post-with-link").filter({ hasText: "音乐" });
            const musicDisplayWrap = musicFormItem.locator(".link-display-wrap").first();
            await musicDisplayWrap.click();
            const searchInput = musicFormItem.locator('.weui-desktop-search input[type="text"]').first();
            const searchTxt = `${params.music.name} - ${params.music.authorName}`;
            await searchInput.fill(searchTxt); // 等待填充完成
            await page.waitForTimeout(300); // 等待输入稳定
            await searchInput.press("Enter");
            await page.waitForTimeout(2000);
            const bgmItemWrap = musicFormItem
                .locator(".bgm-item-wrap") // .song-info .title
                .filter({ hasText: searchTxt })
                .first();
            await bgmItemWrap.hover();
            await page.waitForTimeout(500);
            await bgmItemWrap.locator(".selected-icon").first().click({ force: true });
        }

        //  定时发布
        if (!params.isImmediatelyPublish && params.scheduledPublish) {
            task.logger.info("设置定时发布");
            const timingRadio = page
                .locator(".weui-desktop-form__check-content")
                .filter({ hasNotText: "不定时" })
                .first();
            await timingRadio.click();
            await page.waitForTimeout(500);
            const instance = page.locator('.weui-desktop-picker__date input[placeholder*="请选择发表时间"]');
            await instance.click();
            const dateD = TimeFormatter.format(params.scheduledPublish, "d");
            const nowMonth = TimeFormatter.format(Date.now(), "MM月");
            const nowMonthText = TimeFormatter.format(Date.now(), "M月");
            const month = TimeFormatter.format(params.scheduledPublish, "MM月");
            const monthLocator = await page
                .locator("weui-desktop-picker__panel__label")
                .filter({ hasText: month })
                .first();
            if (!monthLocator) {
                await page.locator(".weui-desktop-picker__panel__label").filter({ hasText: nowMonth }).first().click();
                await page.waitForTimeout(500);
                await page
                    .locator(".weui-desktop-picker__table-row td a")
                    .filter({ hasText: nowMonthText })
                    .first()
                    .click();
            }
            await page.waitForTimeout(500);
            await page.locator(".weui-desktop-picker__table-row td a").filter({ hasText: dateD }).first().click();

            await page
                .locator(".weui-desktop-form__input-wrp input[placeholder*='请选择时间']")
                .fill(TimeFormatter.format(params.scheduledPublish, "hh:mm"));
            await page.locator("i.weui-desktop-icon__time").click();
            // 关闭弹出框
            await page.locator(".post-time-wrap .form-item .label").filter({ hasText: "发表时间" }).click();
        }

        task.logger.info("准备发布...");
        await page.waitForTimeout(300);

        let articleId = "";
        let message = "";
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            const url = response.url();
            if (url.includes("/post/post_create") || url.includes("/post/post_draft")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                articleId = jsonResponse.object?.id || jsonResponse.data?.objectId || "";
                message = jsonResponse?.errMsg || "";
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);
        task._timerRecord.PrePublish = Date.now();

        // 点击发布按钮
        const publishText = params.publishType === "1" ? "发表" : "保存草稿";
        await page.locator(".form-btns .weui-desktop-btn").filter({ hasText: publishText }).first().click();
        task.logger.info("已点击发布按钮，等待响应...");

        // 关闭页面
        await page.close();
        return success(articleId, message || "发布成功");
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        task.logger.error(`微信视频号发布失败: ${errorMsg}`);
        return response(500, `微信视频号发布失败: ${errorMsg}`, "");
    }
};
